import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { DocumentCategory } from "./onboardingSlice";

export interface OwnEmployeeProfile {
  id: number;
  emp_code: string;
  first_name: string;
  middle_name: string | null;
  last_name: string;
  email_address: string;
  contact: string;
  alternate_contact: string | null;
  date_of_joining: string;
  date_of_resignation: string | null;
  last_working_day: string | null;
  status: string;
}

interface EmployeeDocumentsState {
  profile: OwnEmployeeProfile | null;
  profileLoading: boolean;
  profileError: string | null;
  categories: DocumentCategory[];
  checklistLoading: boolean;
  uploadingTypeId: number | null;
}

const initialState: EmployeeDocumentsState = {
  profile: null,
  profileLoading: false,
  profileError: null,
  categories: [],
  checklistLoading: false,
  uploadingTypeId: null,
};

export const fetchOwnProfile = createAsyncThunk(
  "employeeDocuments/fetchOwnProfile",
  async (_, { rejectWithValue }) => {
    const res = await fetch("/api/employee/profile");
    const data = await res.json();
    if (!res.ok) return rejectWithValue(data.error || "Failed to load your profile.");
    return data.employee as OwnEmployeeProfile;
  }
);

export const fetchOwnDocumentChecklist = createAsyncThunk(
  "employeeDocuments/fetchOwnChecklist",
  async (_, { rejectWithValue }) => {
    const res = await fetch("/api/employee/documents");
    const data = await res.json();
    if (!res.ok) return rejectWithValue(data.error || "Failed to load your documents.");
    return data.categories as DocumentCategory[];
  }
);

export const uploadOwnDocument = createAsyncThunk(
  "employeeDocuments/uploadOwnDocument",
  async (
    args: { documentTypeId: number; documentNumber?: string; file: File },
    { dispatch, rejectWithValue }
  ) => {
    const formData = new FormData();
    formData.append("documentTypeId", String(args.documentTypeId));
    if (args.documentNumber) formData.append("documentNumber", args.documentNumber);
    formData.append("file", args.file);

    const res = await fetch("/api/employee/documents", {
      method: "POST",
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) return rejectWithValue(data.error || "Failed to upload document.");

    await dispatch(fetchOwnDocumentChecklist());
    return true;
  }
);

const employeeDocumentsSlice = createSlice({
  name: "employeeDocuments",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchOwnProfile.pending, (state) => {
        state.profileLoading = true;
        state.profileError = null;
      })
      .addCase(fetchOwnProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.profile = action.payload;
      })
      .addCase(fetchOwnProfile.rejected, (state, action) => {
        state.profileLoading = false;
        state.profile = null;
        state.profileError = (action.payload as string) || "Failed to load your profile.";
      })
      .addCase(fetchOwnDocumentChecklist.pending, (state) => {
        state.checklistLoading = true;
      })
      .addCase(fetchOwnDocumentChecklist.fulfilled, (state, action) => {
        state.checklistLoading = false;
        state.categories = action.payload;
      })
      .addCase(fetchOwnDocumentChecklist.rejected, (state) => {
        state.checklistLoading = false;
      })
      .addCase(uploadOwnDocument.pending, (state, action) => {
        state.uploadingTypeId = action.meta.arg.documentTypeId;
      })
      .addCase(uploadOwnDocument.fulfilled, (state) => {
        state.uploadingTypeId = null;
      })
      .addCase(uploadOwnDocument.rejected, (state) => {
        state.uploadingTypeId = null;
      });
  },
});

export default employeeDocumentsSlice.reducer;
